import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box, ButtonBase, Stack, Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts';

import { TOKENS } from '../../../theme/muiTheme';
import { formatPercent, formatTonne } from '../../../shared/utils/number';

const DISTRIBUTION_COLORS = {
  scope3Cat1: '#10b981',
  scope3Cat6: '#8b5cf6',
  scope3Cat2: '#f43f5e',
  scope3Cat7: '#06b6d4',
  scope3Cat8: '#6366f1',
  scope2: '#f97316',
  scope3Cat5: '#94a3b8',
  scope3Cat3: '#f59e0b',
  scope1: '#3b82f6',
};

const recommendationScopeForDistribution = (distributionId) => {
  if (distributionId === 'scope1' || distributionId === 'scope2') return distributionId;
  return String(distributionId).startsWith('scope3') ? 'scope3' : null;
};

function TreemapCell(props) {
  const {
    depth, x, y, width, height, id, shortLabel, label, percent,
    recommendationScope, findRecommendationsLabel, onFindRecommendations,
  } = props;
  if (depth !== 1 || width <= 0 || height <= 0) return null;

  const color = DISTRIBUTION_COLORS[id] || TOKENS.primary;
  const showName = width > 125 && height > 65;
  const showPercent = width > 56 && height > 35;
  const canOpenRecommendations = Boolean(recommendationScope && onFindRecommendations);
  const openRecommendationsWithKeyboard = (event) => {
    if (!canOpenRecommendations || !['Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    onFindRecommendations(recommendationScope);
  };

  return (
    <g
      aria-label={canOpenRecommendations ? `${findRecommendationsLabel}: ${label}` : undefined}
      onClick={canOpenRecommendations
        ? () => onFindRecommendations(recommendationScope)
        : undefined}
      onKeyDown={openRecommendationsWithKeyboard}
      role={canOpenRecommendations ? 'button' : undefined}
      style={{ cursor: canOpenRecommendations ? 'pointer' : 'default' }}
      tabIndex={canOpenRecommendations ? 0 : undefined}
    >
      <rect
        x={x + 2}
        y={y + 2}
        width={Math.max(width - 4, 0)}
        height={Math.max(height - 4, 0)}
        rx={6}
        ry={6}
        fill={color}
        stroke="#fff"
        strokeWidth={2}
      />
      {showName && (
        <text
          x={x + width / 2}
          y={y + height / 2 - 5}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontSize={13}
          fontWeight={700}
        >
          {shortLabel}
        </text>
      )}
      {showPercent && (
        <text
          x={x + width / 2}
          y={y + height / 2 + (showName ? 14 : 1)}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontSize={showName ? 15 : 11}
          fontWeight={800}
        >
          {formatPercent(percent)}
        </text>
      )}
    </g>
  );
}

function DistributionTooltip({ active, payload }) {
  if (!active || !payload?.[0]?.payload) return null;
  const item = payload[0].payload;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        boxShadow: 3,
        maxWidth: 260,
        p: 1.25,
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 750, mb: 0.5 }}>
        {item.label}
      </Typography>
      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>
        {formatTonne(item.value, { withUnit: true })} · {formatPercent(item.percent)}
      </Typography>
    </Box>
  );
}

export default function DistributionChart({
  data, findRecommendationsLabel = '', onFindRecommendations,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const chartData = [...data].sort((left, right) => right.value - left.value).map((item) => ({
    ...item,
    label: t(item.labelKey),
    shortLabel: t(item.shortLabelKey),
    percent: total ? (item.value / total) * 100 : 0,
    recommendationScope: recommendationScopeForDistribution(item.id),
  }));

  if (total <= 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ color: 'text.secondary', minHeight: 220, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t('dashboard.chart.distributionEmpty')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          height: { xs: 280, sm: 310, md: 330 },
          minWidth: 0,
          '& g[role="button"]:focus rect': {
            stroke: theme.palette.text.primary,
            strokeWidth: 3,
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={chartData}
            dataKey="value"
            content={(
              <TreemapCell
                findRecommendationsLabel={findRecommendationsLabel}
                onFindRecommendations={onFindRecommendations}
              />
            )}
            isAnimationActive={false}
          >
            <Tooltip content={<DistributionTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      </Box>

      <Stack spacing={0.75} sx={{ maxHeight: 230, overflowY: 'auto', pr: 0.5 }}>
        {chartData.map((item, index) => {
          const color = DISTRIBUTION_COLORS[item.id] || TOKENS.primary;
          const highlighted = index < 3;
          const canOpenRecommendations = Boolean(
            item.recommendationScope && onFindRecommendations,
          );
          const row = (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                bgcolor: highlighted ? alpha(color, theme.palette.mode === 'dark' ? 0.14 : 0.07) : 'transparent',
                border: 1,
                borderColor: highlighted ? alpha(color, 0.65) : 'divider',
                borderRadius: 1.5,
                minHeight: 36,
                px: 1,
                py: 0.65,
                textAlign: 'left',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: highlighted ? color : 'action.selected',
                  borderRadius: '50%',
                  color: highlighted ? '#fff' : 'text.secondary',
                  display: 'flex',
                  flex: '0 0 auto',
                  fontSize: 10,
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 800,
                  height: 20,
                  justifyContent: 'center',
                  width: 20,
                }}
              >
                {index + 1}
              </Box>
              <Box sx={{ bgcolor: color, borderRadius: '2px', flex: '0 0 auto', height: 8, width: 8 }} />
              <Typography
                variant="caption"
                sx={{ flex: 1, fontWeight: highlighted ? 700 : 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {item.label}
              </Typography>
              <Typography variant="caption" sx={{ color, flexShrink: 0, fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>
                {formatTonne(item.value)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color, flexShrink: 0, fontVariantNumeric: 'tabular-nums', fontWeight: 800, textAlign: 'right', width: 48 }}
              >
                {formatPercent(item.percent)}
              </Typography>
            </Stack>
          );

          if (!canOpenRecommendations) {
            return <MuiTooltip key={item.id} title={item.label} placement="left">{row}</MuiTooltip>;
          }

          return (
            <MuiTooltip key={item.id} title={`${findRecommendationsLabel}: ${item.label}`} placement="left">
              <ButtonBase
                aria-label={`${findRecommendationsLabel}: ${item.label}`}
                onClick={() => onFindRecommendations(item.recommendationScope)}
                sx={{ borderRadius: 1.5, display: 'block', width: '100%' }}
              >
                {row}
              </ButtonBase>
            </MuiTooltip>
          );
        })}
      </Stack>
    </Stack>
  );
}
